/**
 * Workouts — restructured 2-level navigation.
 *   Level 1: module tabs (Gym / Home / Yoga / Zumba)
 *   Level 2: sub-tabs (My Plans / Library / Programs)
 *   Persistent FAB: Generate
 *
 * Session persistence:
 *   - sessionStorage["ff_workouts_module"] — last module
 *   - sessionStorage["ff_workouts_subtab_<module>"] — last sub-tab per module
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, ChevronRight, Plus } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import ExerciseCard from "../../components/workout/ExerciseCard";
import { MUSCLE_GROUPS } from "../../data/muscleGroups";
import GenerateSheet from "../../components/workout/GenerateSheet";

const MODULES = [
  { id: "gym", label: "Gym" },
  { id: "home", label: "Home" },
  { id: "yoga", label: "Yoga" },
  { id: "zumba", label: "Zumba" },
];

const SUB_TABS = [
  { id: "plans", label: "My Plans" },
  { id: "library", label: "Library" },
  { id: "programs", label: "Programs" },
];

const SS_MODULE = "ff_workouts_module";
const SS_SUBTAB = (m) => `ff_workouts_subtab_${m}`;

export default function Workouts() {
  const [module, setModuleState] = useState(() => {
    try {
      return sessionStorage.getItem(SS_MODULE) || "gym";
    } catch {
      return "gym";
    }
  });
  const [subtab, setSubtabState] = useState(() => {
    try {
      return sessionStorage.getItem(SS_SUBTAB(module)) || "plans";
    } catch {
      return "plans";
    }
  });
  const [genOpen, setGenOpen] = useState(false);

  const setModule = (next) => {
    setModuleState(next);
    try {
      sessionStorage.setItem(SS_MODULE, next);
    } catch {}
    // Reset to My Plans on module change, but remember per-module sub-tab
    let prev = "plans";
    try {
      prev = sessionStorage.getItem(SS_SUBTAB(next)) || "plans";
    } catch {}
    setSubtabState(prev);
  };

  const setSubtab = (next) => {
    setSubtabState(next);
    try {
      sessionStorage.setItem(SS_SUBTAB(module), next);
    } catch {}
  };

  return (
    <div className="app-page" data-testid="workouts-page">
      <TopBar title="Workouts" />

      <ModuleTabs active={module} onChange={setModule} />
      <SubTabs active={subtab} onChange={setSubtab} />

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-10">
        {subtab === "plans" && <MyPlansPanel module={module} />}
        {subtab === "library" && <LibraryPanel module={module} />}
        {subtab === "programs" && <ProgramsPanel module={module} />}
      </main>

      <GenerateFAB onClick={() => setGenOpen(true)} />

      <GenerateSheet
        open={genOpen}
        onClose={() => setGenOpen(false)}
        defaultModule={module}
      />

      <BottomNav />
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// LEVEL 1 — Module tabs
// ───────────────────────────────────────────────────────────
function ModuleTabs({ active, onChange }) {
  return (
    <div
      className="max-w-2xl mx-auto"
      style={{ padding: "12px 16px" }}
      data-testid="module-tabs"
    >
      <div className="flex gap-2 justify-between">
        {MODULES.map((m) => {
          const on = active === m.id;
          return (
            <button
              key={m.id}
              onClick={() => onChange(m.id)}
              data-testid={`module-${m.id}`}
              className="font-barlow grow"
              style={{
                background: on ? "var(--accent-blue)" : "transparent",
                color: on ? "#F4F3F1" : "var(--text-secondary)",
                border: on ? "1px solid var(--accent-blue)" : "1px solid var(--border-strong)",
                borderRadius: 100,
                fontSize: 13,
                fontWeight: on ? 700 : 600,
                height: 38,
                padding: "0 20px",
                transition: "all 180ms ease-out",
                whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// LEVEL 2 — Sub tabs (My Plans / Library / Programs)
// ───────────────────────────────────────────────────────────
function SubTabs({ active, onChange }) {
  return (
    <div
      className="max-w-2xl mx-auto"
      style={{ padding: "6px 16px 0" }}
      data-testid="sub-tabs"
    >
      <div className="flex gap-2">
        {SUB_TABS.map((t) => {
          const on = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              data-testid={`subtab-${t.id}`}
              className="font-barlow flex-1 relative"
              style={{
                background: "transparent",
                color: on ? "var(--text-primary)" : "var(--text-tertiary)",
                fontSize: 14,
                fontWeight: on ? 700 : 500,
                height: 40,
              }}
            >
              {t.label}
              {on && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 bottom-0"
                  style={{
                    width: 28,
                    height: 2,
                    background: "var(--accent-blue)",
                    borderRadius: 2,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div style={{ height: 1, background: "#E2E0DC" }} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// MY PLANS panel
// ───────────────────────────────────────────────────────────
function MyPlansPanel({ module }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    setPlans(null);
    apiClient
      .get(`/workout/plans`, { params: { module } })
      .then((r) => setPlans(r.data))
      .catch(() => setPlans([]));
  }, [module]);

  if (plans === null) return <SkeletonList />;

  if (!plans.length) {
    return (
      <EmptyState
        headline="No plans yet"
        body="Start a workout or generate one to see it here."
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="plans-list">
      {plans.map((p) => (
        <button
          key={p.plan_id}
          onClick={() => navigate(`/workouts/session/${p.plan_id}`)}
          data-testid={`plan-${p.plan_id}`}
          className="w-full text-left bg-white rounded-[16px] p-4 border border-line2 transition-all hover:bg-canvas2"
          style={{ boxShadow: "var(--shadow-soft)" }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-display text-[18px]">{p.plan_name}</div>
              <div className="text-[12px] text-ink-soft mt-0.5">
                {p.duration_minutes} min · {p.main_sets?.length || 0} exercises · {p.level}
              </div>
            </div>
            <span
              className="px-3.5 h-9 grid place-items-center rounded-pill text-[12px] font-medium"
              style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}
            >
              Resume
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// LIBRARY panel — module-aware
// ───────────────────────────────────────────────────────────
function LibraryPanel({ module }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    setItems(null);
    if (module !== "gym" && module !== "home") {
      // No library content for yoga/zumba yet
      setItems([]);
      return;
    }
    const params = { module, limit: 200 };
    if (q) params.search = q;
    apiClient.get("/exercises", { params }).then((r) => {
      // Sort by difficulty: beginner first
      const order = { beginner: 0, intermediate: 1, advanced: 2 };
      const sorted = [...r.data].sort(
        (a, b) => (order[a.difficulty] ?? 9) - (order[b.difficulty] ?? 9)
      );
      setItems(sorted);
    });
  }, [module, q]);

  if (items === null) return <SkeletonList />;

  if (!items.length) {
    return (
      <EmptyState
        headline="Content coming soon"
        body="We are building this library. Check back shortly."
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="library-panel">
      {/* Browse-by-muscle-group tiles for Gym module only */}
      {module === "gym" && (
        <section>
          <h2 className="font-barlow-cond uppercase tracking-wider text-[14px] text-ink-muted mb-2">
            Browse by group
          </h2>
          <div className="grid grid-cols-2 gap-3" data-testid="muscle-tiles">
            {MUSCLE_GROUPS.slice(0, 6).map((g) => (
              <button
                key={g.id}
                onClick={() => navigate(`/workouts/muscle/${g.id}`)}
                data-testid={`muscle-tile-${g.id}`}
                className="relative h-[110px] rounded-[16px] overflow-hidden text-left"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <img src={g.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)" }}
                />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
                  <div className="font-display text-[18px] leading-tight">{g.label}</div>
                  <ChevronRight size={14} className="opacity-80" />
                </div>
              </button>
            ))}
            <button
              onClick={() => navigate(`/workouts/muscle/${MUSCLE_GROUPS[6].id}`)}
              data-testid={`muscle-tile-${MUSCLE_GROUPS[6].id}`}
              className="relative h-[110px] rounded-[16px] overflow-hidden text-left col-span-2"
              style={{ boxShadow: "var(--shadow-soft)" }}
            >
              <img src={MUSCLE_GROUPS[6].image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 100%)" }} />
              <div className="absolute bottom-4 left-4 right-4 text-white flex items-end justify-between">
                <div>
                  <div className="font-display text-[20px] leading-tight">{MUSCLE_GROUPS[6].label}</div>
                  <div className="text-[12px] text-white/80 mt-0.5">{MUSCLE_GROUPS[6].tagline}</div>
                </div>
                <ChevronRight size={16} className="opacity-80" />
              </div>
            </button>
          </div>
        </section>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 bg-white rounded-pill h-12 px-4 border border-line">
        <Search size={18} className="text-ink-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${module} exercises…`}
          data-testid="library-search"
          className="flex-1 bg-transparent outline-none text-[14px]"
        />
      </div>

      {/* All exercises */}
      <div className="space-y-2.5">
        <p className="text-[12px] text-ink-muted uppercase tracking-wider">
          {items.length} exercise{items.length === 1 ? "" : "s"}
        </p>
        {items.map((ex) => (
          <ExerciseCard
            key={ex.exercise_id}
            exercise={ex}
            onClick={() => navigate(`/workouts/exercise/${ex.exercise_id}`)}
            action={<span className="text-[12px] capitalize text-ink-soft">{ex.equipment}</span>}
          />
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// PROGRAMS panel — empty state for now
// ───────────────────────────────────────────────────────────
function ProgramsPanel({ module }) {
  return (
    <EmptyState
      headline="Programs coming soon"
      body="Structured programs for this module are on the way."
    />
  );
}

// ───────────────────────────────────────────────────────────
// Empty state per spec
// ───────────────────────────────────────────────────────────
function EmptyState({ headline, body }) {
  return (
    <div
      className="text-center mx-auto"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E0DC",
        borderRadius: 16,
        padding: "32px 24px",
        maxWidth: 460,
      }}
      data-testid="empty-state"
    >
      <div
        className="font-barlow-cond uppercase"
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: "var(--text-primary)",
          letterSpacing: "0.01em",
        }}
      >
        {headline}
      </div>
      <p
        className="font-barlow"
        style={{
          fontSize: 14,
          fontWeight: 400,
          color: "var(--text-secondary)",
          marginTop: 8,
        }}
      >
        {body}
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// Persistent FAB
// ───────────────────────────────────────────────────────────
function GenerateFAB({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="generate-fab"
      className="font-barlow fixed flex items-center gap-1.5"
      style={{
        right: 20,
        bottom: "calc(80px + env(safe-area-inset-bottom))", // above 64px nav + 16px gap
        background: "var(--accent-blue)",
        color: "#F4F3F1",
        border: "1px solid var(--accent-blue-pressed)",
        borderRadius: 100,
        height: 48,
        padding: "0 20px",
        fontSize: 13,
        fontWeight: 700,
        zIndex: 45,
        boxShadow: "0 4px 18px rgba(0, 212, 255, 0.35)",
      }}
    >
      <Plus size={16} strokeWidth={2.6} />
      Generate
    </button>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2.5">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-24 bg-white rounded-[16px] border border-line2 animate-pulse" />
      ))}
    </div>
  );
}
